import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SecurityStatus = ({ securityScore, features }) => {
  const navigate = useNavigate();

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-success';
    if (score >= 60) return 'text-warning';
    return 'text-error';
  };

  const getScoreBackground = (score) => {
    if (score >= 80) return 'bg-success/20';
    if (score >= 60) return 'bg-warning/20';
    return 'bg-error/20';
  };

  return (
    <div className="bg-card rounded-xl p-6 md:p-8 shadow-md border border-border">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl md:text-2xl font-bold text-foreground">Security Status</h3>
        <Button
          variant="outline"
          size="sm"
          iconName="Shield"
          iconPosition="left"
          onClick={() => navigate('/security-center')}
        >
          Manage
        </Button>
      </div>
      <div className="flex items-center gap-6 mb-6 p-4 bg-muted rounded-lg">
        <div className={`w-20 h-20 md:w-24 md:h-24 rounded-full ${getScoreBackground(securityScore)} flex items-center justify-center flex-shrink-0`}>
          <span className={`text-2xl md:text-3xl font-bold ${getScoreColor(securityScore)}`}>
            {securityScore}
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-base md:text-lg font-semibold text-foreground mb-1">
            Security Score
          </p>
          <p className="text-sm md:text-base text-muted-foreground">
            {securityScore >= 80 ? 'Excellent security' : securityScore >= 60 ? 'Good security' : 'Needs improvement'}
          </p>
        </div>
      </div>
      <div className="space-y-3">
        {features?.map((feature) => (
          <div 
            key={feature?.id} 
            className="flex items-center justify-between p-4 bg-muted rounded-lg"
          >
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <Icon 
                name={feature?.enabled ? 'CheckCircle2' : 'AlertCircle'} 
                size={20} 
                className={feature?.enabled ? 'text-success' : 'text-warning'}
              />
              <div className="min-w-0">
                <p className="text-sm md:text-base font-medium text-foreground truncate">
                  {feature?.name}
                </p>
                <p className="text-xs md:text-sm text-muted-foreground">
                  {feature?.description}
                </p>
              </div>
            </div>
            <div className={`px-3 py-1 rounded-full text-xs font-medium flex-shrink-0 ml-2 ${feature?.enabled ? 'bg-success/20 text-success' : 'bg-muted-foreground/20 text-muted-foreground'}`}>
              {feature?.enabled ? 'Enabled' : 'Disabled'}
            </div>
          </div>
        ))}
      </div>
      {securityScore < 80 && (
        <div className="mt-6 p-4 bg-warning/10 border border-warning/30 rounded-lg">
          <div className="flex gap-3">
            <Icon name="AlertTriangle" size={20} className="text-warning flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm md:text-base font-semibold text-foreground mb-1">
                Improve Your Security
              </p>
              <p className="text-xs md:text-sm text-muted-foreground mb-3">
                Enable additional security features to protect your account and assets.
              </p>
              <Button
                variant="warning"
                size="sm"
                onClick={() => navigate('/security-center')}
              >
                Enhance Security
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SecurityStatus;